import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { TtoCampaignVideoDto } from '@/app/common/general-types/tiktok-one-reporting.dto';

interface GenderRow {
  label: string;
  color: string;
  percentage: string;
}

const GENDER_CONFIG: Record<string, { label: string; color: string }> = {
  MALE:   { label: 'Male',   color: '#6508fb' },
  FEMALE: { label: 'Female', color: '#9e8cfc' },
};

const GENDER_OTHER = { label: 'Other', color: '#c4b5fd' };

function getGenderConfig(gender: string | null): { label: string; color: string } {
  return (gender ? GENDER_CONFIG[gender] : null) ?? GENDER_OTHER;
}

interface BarRow {
  label: string;
  percentage: string;
  widthPct: number;
}

const AGE_LABELS: Record<string, string> = {
  '1': '13 - 17',
  '2': '18 - 24',
  '3': '25 - 34',
  '4': '35 - 44',
  '5': '45 - 54',
  '6': '55+',
};

@Component({
  selector: 'vat-analysis-viewers',
  imports: [NgxEchartsDirective],
  templateUrl: './analysis-viewers.html',
  styleUrl: './analysis-viewers.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisViewers {
  readonly video = input.required<TtoCampaignVideoDto>();

  readonly genderChartOption = computed<EChartsOption>(() => {
    const raw = this.video().audience_genders_distribution as
      Array<{ gender: string | null; percentage: number }> | null;
    if (!raw?.length) return {};

    return {
      tooltip: { trigger: 'item' },
      series: [{
        type: 'pie',
        radius: ['38%', '100%'],
        startAngle: 180,
        endAngle: 360,
        center: ['50%', '70%'],
        itemStyle: {
          borderRadius: 10,
          // borderColor: '#fff',
          borderColor: 'transparent',
          borderWidth: 2,
        },
        data: raw.map(g => {
          const cfg = getGenderConfig(g.gender);
          return {
            name: cfg.label,
            value: parseFloat((g.percentage * 100).toFixed(1)),
            itemStyle: { color: cfg.color },
          };
        }),
        label: { show: false },
        emphasis: { scale: false },
      }],
    };
  });

  readonly genderRows = computed<GenderRow[]>(() => {
    const raw = this.video().audience_genders_distribution as
      Array<{ gender: string | null; percentage: number }> | null;
    if (!raw?.length) return [];

    return [...raw]
      .sort((a, b) => b.percentage - a.percentage)
      .map(g => ({
        ...getGenderConfig(g.gender),
        percentage: `${Math.round(g.percentage * 100)}%`,
      }));
  });

  readonly ageGroups = computed<BarRow[]>(() => {
    const raw = this.video().audience_age_distribution as
      Array<{ age: string; percentage: number }> | null;
    if (!raw?.length) return [];
    return [...raw]
      .sort((a, b) => Number(a.age) - Number(b.age))
      .map(a => {
        const pct = a.percentage * 100;
        return {
          label: AGE_LABELS[a.age] ?? a.age,
          percentage: `${Math.round(pct)}%`,
          widthPct: pct,
        };
      });
  });

  readonly locations = computed<BarRow[]>(() => {
    const raw = this.video().audience_countries_distribution as
      Array<{ country: string; percentage: number }> | null;
    if (!raw?.length) return [];

    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    const sorted = [...raw].sort((a, b) => b.percentage - a.percentage);
    const top = sorted.slice(0, 10);
    const rest = sorted.slice(10);

    const rows: BarRow[] = top.map(c => {
      const pct = c.percentage * 100;
      return {
        label: regionNames.of(c.country) ?? c.country,
        percentage: `${parseFloat(pct.toFixed(1))}%`,
        widthPct: pct,
      };
    });

    if (rest.length > 0) {
      const otherPct = rest.reduce((sum, c) => sum + c.percentage, 0) * 100;
      rows.push({
        label: 'Other',
        percentage: `${parseFloat(otherPct.toFixed(1))}%`,
        widthPct: otherPct,
      });
    }

    return rows;
  });

}
